// Sensor lifecycle (design: the measuring point is the unit of record; a
// sensor is installed at a point for a period). Until the platform has
// measuring points, every installation is a tenant app record (app
// "coldchain", kind "installation") on the sensor: where it went (account,
// store labels, cabinet name, regime) and from when. A return to HQ stock is
// an installation with stock: true. The cabinet pages read the data of the
// current installation only, so a move or swap never mixes two cabinets.
import { api } from "@gimasi/giothub-shell";
import { tenantApi } from "../lib/account";
import { REGIMES } from "./classes";

export interface Placement { account_id: string; city: string; area: string; site: string; cabinet: string; regime: string; stock?: boolean; room?: boolean }
export interface Install { id: string; device_id: string | null; device_serial: string; occurred_at: string; author_name: string; body: Placement }

const slug = (x: string) => x.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

/** every installation record, newest first */
export async function loadInstalls(): Promise<Install[]> {
  const r = await tenantApi<{ items: Install[] }>("GET", "/records?app=coldchain&kind=installation&limit=500");
  return r.items;
}
/** the current installation per device (the newest record) */
export function currentInstalls(all: Install[]) {
  const m = new Map<string, Install>();
  for (const x of all) if (x.device_id && !m.has(x.device_id)) m.set(x.device_id, x);
  return m;
}

/**
 * Install (or move) a sensor: owner first (transfer to another account,
 * reorg inside the same account tree branch is left to the API's rules),
 * then name + labels, then the installation record.
 */
export async function install(deviceId: string, currentOwner: string | null, p: Placement, reason: "transfer" | "reorg" | "return") {
  // labels first: if the API refuses one, nothing has moved yet. Cabinet or room is kept in the
  // installation record (body.room), not in a label: the tenant's "position" is a geo property.
  const labels = p.stock
    ? { city: null, area: null, site: null, zone: null, cold_class: null, temperature_regime: null }
    : { country: "CH", city: slug(p.city), area: slug(p.area), site: slug(p.site), zone: p.room ? slug(p.cabinet) : null, cold_class: p.regime || null, temperature_regime: (p.regime && REGIMES[p.regime]?.regime) || null };
  await api.patchDevice(deviceId, { name: p.cabinet || undefined, attributes: labels });
  if (p.account_id && p.account_id !== currentOwner) {
    await tenantApi("POST", `/devices/${deviceId}/owner`, { account_id: p.account_id, reason });
  }
  return tenantApi<Install>("POST", "/records", {
    app: "coldchain", kind: "installation", device_id: deviceId, status: p.stock ? "stock" : "installed",
    title: p.stock ? "HQ stock" : `${p.cabinet} · ${p.site}`, body: { ...p, city: slug(p.city), area: slug(p.area), site: slug(p.site) },
  });
}
